import {
  GRID_COLUMNS,
  MIN_COLUMN_SPAN,
  defaultOrgDashboardLayout,
} from "../constants/org-dashboard";
import {
  DashboardCardItem,
  DashboardColumn,
  DashboardItem,
  DashboardLayout,
  DashboardPath,
  DashboardSplitItem,
  isSplitItem,
} from "../types/org-dashboard";

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/**
 * Every card currently on the layout, in reading order. Used to keep the
 * catalogue in step with the canvas.
 */
export const cardsInLayout = (layout: DashboardLayout): string[] => {
  const ids: string[] = [];
  layout.columns.forEach((column) =>
    column.items.forEach((item) => {
      if (isSplitItem(item)) {
        item.parts.forEach((part) => part.items.forEach((c) => ids.push(c.id)));
      } else {
        ids.push(item.id);
      }
    }),
  );
  return ids;
};

/** The list an item lives in, so moves and removals share one code path. */
const listAt = (
  layout: DashboardLayout,
  path: DashboardPath,
): DashboardItem[] | DashboardCardItem[] | undefined => {
  const column = layout.columns[path.column];
  if (!column) return undefined;
  if (path.part === undefined) return column.items;
  const item = column.items[path.item];
  if (!isSplitItem(item)) return undefined;
  return item.parts[path.part]?.items;
};

/**
 * Two invariants, applied after every change:
 * columns add up to twelve, and a split adds up to its column. A column too
 * narrow to hold a split gives up the split and keeps the cards.
 */
export const normaliseLayout = (layout: DashboardLayout): DashboardLayout => {
  const next = clone(layout);

  next.columns = next.columns.filter((column) => column.width > 0);
  if (!next.columns.length) return clone(defaultOrgDashboardLayout);

  const total = next.columns.reduce((sum, c) => sum + c.width, 0);
  if (total !== GRID_COLUMNS) {
    // Give any difference to the widest column so the row still fills the grid.
    const widest = next.columns.reduce(
      (best, column, index) =>
        column.width > next.columns[best].width ? index : best,
      0,
    );
    next.columns[widest].width = Math.max(
      MIN_COLUMN_SPAN,
      next.columns[widest].width + (GRID_COLUMNS - total),
    );
  }

  next.columns = next.columns.map((column) => {
    const items: DashboardItem[] = [];
    column.items.forEach((item) => {
      if (!isSplitItem(item)) {
        items.push(item);
        return;
      }
      if (column.width < MIN_COLUMN_SPAN * 2) {
        item.parts.forEach((part) => items.push(...part.items));
        return;
      }
      const partsTotal = item.parts[0].width + item.parts[1].width;
      const scaled = Math.round(
        (item.parts[0].width / partsTotal) * column.width,
      );
      const first = Math.min(
        column.width - MIN_COLUMN_SPAN,
        Math.max(MIN_COLUMN_SPAN, scaled),
      );
      item.parts[0].width = first;
      item.parts[1].width = column.width - first;
      items.push(item);
    });
    return { ...column, items };
  });

  return next;
};

/** A column only ever trades width with a neighbour, never with the grid. */
export const resizeColumn = (
  layout: DashboardLayout,
  index: number,
  width: number,
): DashboardLayout => {
  const next = clone(layout);
  const neighbour = index < next.columns.length - 1 ? index + 1 : index - 1;
  if (!next.columns[index] || !next.columns[neighbour]) return layout;

  const delta = width - next.columns[index].width;
  if (!delta) return layout;
  if (next.columns[neighbour].width - delta < MIN_COLUMN_SPAN) return layout;
  if (width < MIN_COLUMN_SPAN) return layout;

  next.columns[index].width = width;
  next.columns[neighbour].width -= delta;
  return normaliseLayout(next);
};

export const resizeSplit = (
  layout: DashboardLayout,
  column: number,
  item: number,
  firstWidth: number,
): DashboardLayout => {
  const next = clone(layout);
  const target = next.columns[column]?.items[item];
  if (!isSplitItem(target)) return layout;

  const total = target.parts[0].width + target.parts[1].width;
  if (firstWidth < MIN_COLUMN_SPAN || total - firstWidth < MIN_COLUMN_SPAN) {
    return layout;
  }
  target.parts[0].width = firstWidth;
  target.parts[1].width = total - firstWidth;
  return normaliseLayout(next);
};

export const addCard = (
  layout: DashboardLayout,
  id: string,
): DashboardLayout => {
  const next = clone(layout);
  // The emptiest column, weighted by width, so wide columns take more cards.
  let target = 0;
  let lowest = Infinity;
  next.columns.forEach((column, index) => {
    const load = column.items.length / column.width;
    if (load < lowest) {
      lowest = load;
      target = index;
    }
  });
  next.columns[target].items.push({ id });
  return normaliseLayout(next);
};

/** Inside a split the card index is `card`; otherwise the card *is* the item. */
const cardIndex = (path: DashboardPath): number =>
  path.part === undefined ? path.item : (path.card ?? 0);

export const removeCard = (
  layout: DashboardLayout,
  path: DashboardPath,
): DashboardLayout => {
  const next = clone(layout);
  const list = listAt(next, path);
  if (!list) return layout;
  list.splice(cardIndex(path), 1);
  return normaliseLayout(next);
};

/** Move a card one place earlier or later inside its own stack. */
export const moveCardWithinStack = (
  layout: DashboardLayout,
  path: DashboardPath,
  offset: -1 | 1,
): DashboardLayout => {
  const next = clone(layout);
  const list = listAt(next, path);
  if (!list) return layout;
  const from = cardIndex(path);
  const to = from + offset;
  if (to < 0 || to >= list.length) return layout;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return normaliseLayout(next);
};

/** Move a card to the end of another column's stack. */
export const moveCardToColumn = (
  layout: DashboardLayout,
  path: DashboardPath,
  column: number,
): DashboardLayout => {
  const next = clone(layout);
  const list = listAt(next, path);
  if (!list || !next.columns[column]) return layout;
  const [moved] = list.splice(cardIndex(path), 1) as DashboardCardItem[];
  next.columns[column].items.push(moved);
  return normaliseLayout(next);
};

/** Lift a card out of a split and onto the end of its column's own stack. */
export const moveCardOutOfSplit = (
  layout: DashboardLayout,
  path: DashboardPath,
): DashboardLayout => {
  if (path.part === undefined) return layout;
  const next = clone(layout);
  const list = listAt(next, path);
  if (!list) return layout;
  const [moved] = list.splice(cardIndex(path), 1) as DashboardCardItem[];
  next.columns[path.column].items.push(moved);
  return normaliseLayout(next);
};

/** Move a card to the other side of the split it is already in. */
export const moveCardAcrossSplit = (
  layout: DashboardLayout,
  path: DashboardPath,
): DashboardLayout => {
  if (path.part === undefined) return layout;
  const next = clone(layout);
  const split = next.columns[path.column]?.items[path.item];
  if (!isSplitItem(split)) return layout;
  const from = split.parts[path.part];
  const [moved] = from.items.splice(cardIndex(path), 1);
  split.parts[path.part === 0 ? 1 : 0].items.push(moved);
  return normaliseLayout(next);
};

/** Split a column's row in two, or fold a split back into plain cards. */
export const splitColumn = (
  layout: DashboardLayout,
  column: number,
): DashboardLayout => {
  const next = clone(layout);
  const target = next.columns[column];
  if (!target || target.width < MIN_COLUMN_SPAN * 2) return layout;
  const first = Math.max(MIN_COLUMN_SPAN, Math.floor(target.width / 2));
  const split: DashboardSplitItem = {
    split: true,
    parts: [
      { width: first, items: [] },
      { width: target.width - first, items: [] },
    ],
  };
  target.items.push(split);
  return normaliseLayout(next);
};

export const removeSplit = (
  layout: DashboardLayout,
  column: number,
  item: number,
): DashboardLayout => {
  const next = clone(layout);
  const target = next.columns[column]?.items[item];
  if (!isSplitItem(target)) return layout;
  const cards = target.parts.flatMap((part) => part.items);
  next.columns[column].items.splice(item, 1, ...cards);
  return normaliseLayout(next);
};

export const addColumn = (layout: DashboardLayout): DashboardLayout => {
  const next = clone(layout);
  const widest = next.columns.reduce(
    (best, column, index) =>
      column.width > next.columns[best].width ? index : best,
    0,
  );
  const take = next.columns[widest].width - MIN_COLUMN_SPAN;
  if (take < MIN_COLUMN_SPAN) return layout;
  next.columns[widest].width -= take;
  next.columns.push({ width: take, items: [] });
  return normaliseLayout(next);
};

/** Removing a column hands its cards and its width to a neighbour. */
export const removeColumn = (
  layout: DashboardLayout,
  index: number,
): DashboardLayout => {
  if (layout.columns.length <= 1) return layout;
  const next = clone(layout);
  const [removed] = next.columns.splice(index, 1);
  const target = index === 0 ? 0 : index - 1;
  next.columns[target].items = next.columns[target].items.concat(removed.items);
  next.columns[target].width += removed.width;
  return normaliseLayout(next);
};

export const moveColumn = (
  layout: DashboardLayout,
  index: number,
  offset: -1 | 1,
): DashboardLayout => {
  const to = index + offset;
  if (to < 0 || to >= layout.columns.length) return layout;
  const next = clone(layout);
  const [moved] = next.columns.splice(index, 1);
  next.columns.splice(to, 0, moved);
  return normaliseLayout(next);
};

/** Guards a stored layout: unknown cards are dropped, shape errors reset it. */
export const parseStoredLayout = (
  raw: string | null,
  knownCards: string[],
): DashboardLayout | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as DashboardLayout;
    if (!parsed || !Array.isArray(parsed.columns) || !parsed.columns.length) {
      return undefined;
    }
    const known = new Set(knownCards);
    const columns: DashboardColumn[] = parsed.columns.map((column) => ({
      width: Number(column.width) || MIN_COLUMN_SPAN,
      items: (Array.isArray(column.items) ? column.items : [])
        .map((item) => {
          if (isSplitItem(item)) {
            return {
              split: true as const,
              parts: [0, 1].map((index) => ({
                width: Number(item.parts?.[index]?.width) || MIN_COLUMN_SPAN,
                items: (item.parts?.[index]?.items ?? []).filter((c) =>
                  known.has(c?.id),
                ),
              })) as DashboardSplitItem["parts"],
            };
          }
          return known.has((item as DashboardCardItem)?.id) ? item : undefined;
        })
        .filter((item): item is DashboardItem => Boolean(item)),
    }));
    return normaliseLayout({ columns });
  } catch {
    return undefined;
  }
};
